/**
 * HTTP client for the SFC platform API.
 *
 * This package speaks HTTP and nothing else. It must not import NestJS, an ORM
 * or any other backend runtime code, so a browser, another Node service or a
 * third-party website can all use the same client.
 */
import { API_VERSION } from "@sfc/contracts";
/** A response the API could not answer, carrying its structured error body. */
export class PlatformApiError extends Error {
    status;
    code;
    requestId;
    constructor(status, code, message, requestId) {
        super(message);
        this.name = "PlatformApiError";
        this.status = status;
        this.code = code;
        this.requestId = requestId;
    }
}
/** The API was unreachable, or did not answer within the configured timeout. */
export class PlatformUnreachableError extends Error {
    constructor(message, options) {
        super(message, options);
        this.name = "PlatformUnreachableError";
    }
}
const DEFAULT_TIMEOUT_MS = 5000;
export function createPlatformClient(options) {
    const origin = options.baseUrl.replace(/\/+$/, "");
    const doFetch = options.fetch ?? globalThis.fetch;
    const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
    /**
     * A multipart body is handed to fetch untouched: the browser has to set the
     * content type itself, because only it knows the boundary it generated.
     */
    function isMultipart(body) {
        return typeof FormData !== "undefined" && body instanceof FormData;
    }
    async function request(path, options) {
        const timeout = AbortSignal.timeout(timeoutMs);
        const combined = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout;
        const method = options.method ?? "GET";
        let response;
        try {
            response = await doFetch(`${origin}/${API_VERSION}${path}`, {
                method,
                headers: {
                    ...options.headers,
                    accept: options.raw ? "*/*" : "application/json",
                    ...(options.body === undefined || isMultipart(options.body)
                        ? {}
                        : { "content-type": "application/json" }),
                },
                // The session is an http-only cookie, so it only travels if the browser
                // is told to send credentials. The API names its allowed origins, and
                // refuses a state-changing request from anywhere else.
                credentials: options.anonymous ? "omit" : "include",
                ...(options.body === undefined
                    ? {}
                    : { body: isMultipart(options.body) ? options.body : JSON.stringify(options.body) }),
                signal: combined,
            });
        }
        catch (cause) {
            throw new PlatformUnreachableError(`The platform API did not answer ${path}.`, { cause });
        }
        const requestId = response.headers.get("x-request-id") ?? undefined;
        if (options.raw && options.okStatuses.includes(response.status))
            return response;
        const body = await response.json().catch(() => undefined);
        if (options.okStatuses.includes(response.status))
            return body;
        const error = body;
        throw new PlatformApiError(response.status, error?.error?.code ?? "unknown_error", error?.error?.message ?? `The platform API answered ${path} with ${response.status}.`, error?.error?.requestId ?? requestId);
    }
    return {
        health: {
            async live(signal) {
                return (await request("/health/live", { signal, okStatuses: [200] }));
            },
            async ready(signal) {
                return (await request("/health/ready", { signal, okStatuses: [200, 503] }));
            },
        },
        publicApplications: {
            async submit(siteSlug, form, idempotencyKey, signal) {
                return (await request(`/public/sites/${encodeURIComponent(siteSlug)}/applications`, {
                    method: "POST", body: form, signal, okStatuses: [200], anonymous: true,
                    headers: { "Idempotency-Key": idempotencyKey },
                }));
            },
        },
        publicEnquiries: {
            async submit(siteSlug, enquiry, idempotencyKey, signal) {
                return (await request(`/public/sites/${encodeURIComponent(siteSlug)}/enquiries`, {
                    method: "POST",
                    body: enquiry,
                    signal,
                    okStatuses: [200],
                    anonymous: true,
                    ...(idempotencyKey ? { headers: { "Idempotency-Key": idempotencyKey } } : {}),
                }));
            },
        },
        enquiries: {
            async list(organizationId, query = {}, signal) {
                const search = new URLSearchParams();
                for (const [key, value] of Object.entries(query)) {
                    if (value)
                        search.set(key, value);
                }
                const suffix = search.size > 0 ? `?${search.toString()}` : "";
                return (await request(`/organizations/${organizationId}/enquiries${suffix}`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async detail(organizationId, enquiryId, signal) {
                return (await request(`/organizations/${organizationId}/enquiries/${enquiryId}`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async setStatus(organizationId, enquiryId, status, signal) {
                return (await request(`/organizations/${organizationId}/enquiries/${enquiryId}`, {
                    method: "PATCH",
                    body: { status },
                    signal,
                    okStatuses: [200],
                }));
            },
        },
        conversations: {
            async list(organizationId, query = {}, signal) {
                const search = new URLSearchParams();
                if (query.before)
                    search.set("before", query.before);
                if (query.limit)
                    search.set("limit", String(query.limit));
                const suffix = search.size > 0 ? `?${search.toString()}` : "";
                return (await request(`/organizations/${organizationId}/conversations${suffix}`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async detail(organizationId, conversationId, signal) {
                return (await request(`/organizations/${organizationId}/conversations/${conversationId}`, { signal, okStatuses: [200] }));
            },
            async erase(organizationId, conversationId, signal) {
                return (await request(`/organizations/${organizationId}/conversations/${conversationId}/deletion`, { method: "POST", signal, okStatuses: [200] }));
            },
        },
        applications: {
            async list(organizationId, query = {}, signal) {
                const search = new URLSearchParams();
                for (const [key, value] of Object.entries(query)) {
                    if (value)
                        search.set(key, value);
                }
                const suffix = search.size > 0 ? `?${search.toString()}` : "";
                return (await request(`/organizations/${organizationId}/applications${suffix}`, {
                    signal, okStatuses: [200],
                }));
            },
            async detail(organizationId, applicationId, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}`, {
                    signal, okStatuses: [200],
                }));
            },
        },
        jobs: {
            async list(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/job-openings`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async create(organizationId, draft, signal) {
                return (await request(`/organizations/${organizationId}/job-openings`, {
                    method: "POST",
                    body: draft,
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async update(organizationId, jobId, changes, signal) {
                return (await request(`/organizations/${organizationId}/job-openings/${jobId}`, {
                    method: "PATCH",
                    body: changes,
                    signal,
                    okStatuses: [200],
                }));
            },
            async publish(organizationId, jobId, signal) {
                return (await request(`/organizations/${organizationId}/job-openings/${jobId}/publish`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async close(organizationId, jobId, signal) {
                return (await request(`/organizations/${organizationId}/job-openings/${jobId}/close`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async remove(organizationId, jobId, signal) {
                await request(`/organizations/${organizationId}/job-openings/${jobId}`, {
                    method: "DELETE",
                    signal,
                    okStatuses: [204],
                });
            },
        },
        journal: {
            async list(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/journal`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async read(organizationId, postId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async create(organizationId, draft, signal) {
                return (await request(`/organizations/${organizationId}/journal`, {
                    method: "POST",
                    body: draft,
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async save(organizationId, postId, changes, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}`, {
                    method: "PATCH",
                    body: changes,
                    signal,
                    okStatuses: [200],
                }));
            },
            async preview(organizationId, postId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/preview`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async remove(organizationId, postId, signal) {
                await request(`/organizations/${organizationId}/journal/${postId}`, {
                    method: "DELETE",
                    signal,
                    okStatuses: [204],
                });
            },
            async publish(organizationId, postId, revisionId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/publish`, {
                    method: "POST",
                    body: revisionId ? { revisionId } : {},
                    signal,
                    okStatuses: [200],
                }));
            },
            async unpublish(organizationId, postId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/unpublish`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async archive(organizationId, postId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/archive`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async restore(organizationId, postId, revisionId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/restore`, {
                    method: "POST",
                    body: { revisionId },
                    signal,
                    okStatuses: [200],
                }));
            },
            async revisions(organizationId, postId, signal) {
                return (await request(`/organizations/${organizationId}/journal/${postId}/revisions`, {
                    signal,
                    okStatuses: [200],
                }));
            },
        },
        review: {
            async correct(organizationId, applicationId, version, changes, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}`, {
                    method: "PATCH",
                    body: { version, ...changes },
                    signal,
                    okStatuses: [200],
                }));
            },
            async changeStatus(organizationId, applicationId, version, status, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}/status`, {
                    method: "POST",
                    body: { version, status },
                    signal,
                    okStatuses: [200],
                }));
            },
            async listNotes(organizationId, applicationId, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}/notes`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async addNote(organizationId, applicationId, body, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}/notes`, {
                    method: "POST",
                    body: { body },
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async listChanges(organizationId, applicationId, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}/changes`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async erase(organizationId, applicationId, reason, signal) {
                return (await request(`/organizations/${organizationId}/applications/${applicationId}/deletion`, {
                    method: "POST",
                    body: reason ? { reason } : {},
                    signal,
                    okStatuses: [200],
                }));
            },
        },
        documents: {
            async list(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/media`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async upload(organizationId, file, signal) {
                const form = new FormData();
                form.append("file", file);
                return (await request(`/organizations/${organizationId}/media`, {
                    method: "POST",
                    body: form,
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async download(organizationId, mediaId, signal) {
                const response = (await request(`/organizations/${organizationId}/media/${mediaId}/download`, {
                    signal,
                    okStatuses: [200],
                    raw: true,
                }));
                return response.blob();
            },
            async remove(organizationId, mediaId, signal) {
                await request(`/organizations/${organizationId}/media/${mediaId}`, {
                    method: "DELETE",
                    signal,
                    okStatuses: [204],
                });
            },
        },
        staff: {
            async listMembers(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/members`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async provisionMember(organizationId, member, signal) {
                return (await request(`/organizations/${organizationId}/members`, {
                    method: "POST",
                    body: member,
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async changeRole(organizationId, membershipId, role, signal) {
                return (await request(`/organizations/${organizationId}/members/${membershipId}`, {
                    method: "PATCH",
                    body: { role },
                    signal,
                    okStatuses: [200],
                }));
            },
            async revokeMember(organizationId, membershipId, signal) {
                await request(`/organizations/${organizationId}/members/${membershipId}`, {
                    method: "DELETE",
                    signal,
                    okStatuses: [204],
                });
            },
            async resetMemberPassword(organizationId, membershipId, signal) {
                return (await request(`/organizations/${organizationId}/members/${membershipId}/password`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async readAudit(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/audit`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async readReports(organizationId, query, signal) {
                const search = new URLSearchParams();
                if (query?.status)
                    search.set("status", query.status);
                if (query?.before)
                    search.set("before", query.before);
                const suffix = search.size > 0 ? `?${search.toString()}` : "";
                return (await request(`/organizations/${organizationId}/match/reports${suffix}`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async readReport(organizationId, reportId, signal) {
                return (await request(`/organizations/${organizationId}/match/reports/${encodeURIComponent(reportId)}`, { signal, okStatuses: [200] }));
            },
            async decideReport(organizationId, reportId, status, signal) {
                return (await request(`/organizations/${organizationId}/match/reports/${encodeURIComponent(reportId)}`, { method: "PATCH", body: { status }, signal, okStatuses: [200] }));
            },
            async removeIdea(organizationId, ideaId, signal) {
                return (await request(`/organizations/${organizationId}/match/ideas/${encodeURIComponent(ideaId)}/removal`, { method: "POST", signal, okStatuses: [200] }));
            },
            async readInvitationQueue(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/match/invitations`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async sendInvitations(organizationId, signal) {
                return (await request(`/organizations/${organizationId}/match/invitations`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async setParticipantAccess(organizationId, participantId, disabled, signal) {
                return (await request(`/organizations/${organizationId}/match/participants/${encodeURIComponent(participantId)}`, { method: "PATCH", body: { disabled }, signal, okStatuses: [200] }));
            },
        },
        match: {
            async requestSignInLink(email, signal) {
                return (await request("/public/match/sign-in", {
                    method: "POST",
                    body: { email },
                    signal,
                    okStatuses: [200, 202],
                }));
            },
            async signInWithPassword(email, password, signal) {
                return (await request("/public/match/sign-in/password", {
                    method: "POST",
                    body: { email, password },
                    signal,
                    okStatuses: [200],
                }));
            },
            async passwordState(signal) {
                return (await request("/public/match/password", {
                    signal,
                    okStatuses: [200],
                }));
            },
            async setPassword(password, currentPassword, signal) {
                return (await request("/public/match/password", {
                    method: "PUT",
                    body: currentPassword ? { password, currentPassword } : { password },
                    signal,
                    okStatuses: [200],
                }));
            },
            async removePassword(signal) {
                return (await request("/public/match/password", {
                    method: "DELETE",
                    signal,
                    okStatuses: [200],
                }));
            },
            async consumeSignInLink(token, signal) {
                return (await request(`/public/match/sign-in/${encodeURIComponent(token)}`, {
                    method: "POST",
                    signal,
                    okStatuses: [200],
                }));
            },
            async current(signal) {
                try {
                    return (await request("/public/match/me", { signal, okStatuses: [200] }));
                }
                catch (error) {
                    // Not signed in is an ordinary state of the world, not a failure the
                    // caller should have to catch. Anything else still throws.
                    if (error instanceof PlatformApiError && error.status === 401)
                        return undefined;
                    throw error;
                }
            },
            async signOut(signal) {
                await request("/public/match/sign-out", { method: "POST", signal, okStatuses: [204] });
            },
            async skills(signal) {
                return (await request("/public/match/skills", {
                    signal,
                    okStatuses: [200],
                }));
            },
            async profile(signal) {
                try {
                    return (await request("/public/match/profile", { signal, okStatuses: [200] }));
                }
                catch (error) {
                    // Not having written one yet is an ordinary state of the world, not
                    // a failure the caller should have to catch.
                    if (error instanceof PlatformApiError && error.status === 404)
                        return undefined;
                    throw error;
                }
            },
            async saveProfile(profile, signal) {
                return (await request("/public/match/profile", {
                    method: "PUT",
                    body: profile,
                    signal,
                    okStatuses: [200],
                }));
            },
            async ideas(signal) {
                return (await request("/public/match/ideas", { signal, okStatuses: [200] }));
            },
            async postIdea(idea, signal) {
                return (await request("/public/match/ideas", {
                    method: "POST",
                    body: idea,
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async updateIdea(ideaId, idea, signal) {
                return (await request(`/public/match/ideas/${encodeURIComponent(ideaId)}`, {
                    method: "PUT",
                    body: idea,
                    signal,
                    okStatuses: [200],
                }));
            },
            async withdrawIdea(ideaId, signal) {
                return (await request(`/public/match/ideas/${encodeURIComponent(ideaId)}/withdrawal`, {
                    method: "POST",
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async deck(signal) {
                return (await request("/public/match/deck", { signal, okStatuses: [200] }));
            },
            async swipe(ideaId, direction, signal) {
                return (await request(`/public/match/deck/${encodeURIComponent(ideaId)}/swipe`, {
                    method: "POST",
                    body: { direction },
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async interest(signal) {
                return (await request("/public/match/interest", {
                    signal,
                    okStatuses: [200],
                }));
            },
            async decide(interestId, outcome, signal) {
                return (await request(`/public/match/interest/${encodeURIComponent(interestId)}/decision`, {
                    method: "POST",
                    body: { outcome },
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async matches(signal) {
                const page = (await request("/public/match/matches", {
                    signal,
                    okStatuses: [200],
                }));
                return page.matches;
            },
            async thread(matchId, signal) {
                return (await request(`/public/match/matches/${encodeURIComponent(matchId)}/messages`, {
                    signal,
                    okStatuses: [200],
                }));
            },
            async sendMessage(matchId, body, signal) {
                return (await request(`/public/match/matches/${encodeURIComponent(matchId)}/messages`, {
                    method: "POST",
                    body: { body },
                    signal,
                    okStatuses: [200, 201],
                }));
            },
            async markRead(matchId, signal) {
                await request(`/public/match/matches/${encodeURIComponent(matchId)}/read`, {
                    method: "POST",
                    signal,
                    okStatuses: [204],
                });
            },
            async reportIdea(ideaId, reason, detail, signal) {
                await request(`/public/match/deck/${encodeURIComponent(ideaId)}/report`, {
                    method: "POST",
                    body: detail ? { reason, detail } : { reason },
                    signal,
                    okStatuses: [204],
                });
            },
            async reportPerson(matchId, reason, detail, signal) {
                await request(`/public/match/matches/${encodeURIComponent(matchId)}/report`, {
                    method: "POST",
                    body: detail ? { reason, detail } : { reason },
                    signal,
                    okStatuses: [204],
                });
            },
            async reportMessage(matchId, messageId, reason, detail, signal) {
                await request(`/public/match/matches/${encodeURIComponent(matchId)}/messages/${encodeURIComponent(messageId)}/report`, {
                    method: "POST",
                    body: detail ? { reason, detail } : { reason },
                    signal,
                    okStatuses: [204],
                });
            },
            async blockAuthor(ideaId, signal) {
                await request(`/public/match/deck/${encodeURIComponent(ideaId)}/block`, {
                    method: "POST",
                    signal,
                    okStatuses: [204],
                });
            },
            async blockMatch(matchId, signal) {
                await request(`/public/match/matches/${encodeURIComponent(matchId)}/block`, {
                    method: "POST",
                    signal,
                    okStatuses: [204],
                });
            },
        },
        auth: {
            async login(credentials, signal) {
                return (await request("/auth/login", {
                    method: "POST",
                    body: credentials,
                    signal,
                    okStatuses: [200],
                }));
            },
            async logout(signal) {
                await request("/auth/logout", { method: "POST", signal, okStatuses: [204] });
            },
            async changePassword(change, signal) {
                return (await request("/auth/password", {
                    method: "POST",
                    body: change,
                    signal,
                    okStatuses: [200],
                }));
            },
            async currentViewer(signal) {
                try {
                    return (await request("/auth/session", { signal, okStatuses: [200] }));
                }
                catch (error) {
                    // Not signed in is an ordinary state of the world, not a failure the
                    // caller should have to catch. Anything else still throws.
                    if (error instanceof PlatformApiError && error.status === 401)
                        return undefined;
                    throw error;
                }
            },
        },
    };
}
//# sourceMappingURL=index.js.map